import React from 'react';
import {
  Layout,
  Space,
  Avatar,
  Button,
  Dropdown,
  Typography,
  type MenuProps,
} from 'antd';
import {
  BellOutlined,
  QuestionCircleOutlined,
  UserOutlined,
  MenuOutlined,
  LogoutOutlined,
  SettingOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { logout } from '../../store/slices/authSlice';
import LanguageSwitcher from '../common/LanguageSwitcher';

const { Header } = Layout;
const { Text } = Typography;

interface GlobalHeaderProps {
  stage: string;
  company: any;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const GlobalHeader: React.FC<GlobalHeaderProps> = ({
  stage,
  company,
  collapsed,
  setCollapsed,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);

  // 检测文本是否包含中文字符
  const hasChinese = (text: string) => /[\u4e00-\u9fff]/.test(text);
  const isNonChineseLocale = i18n.language !== 'zh-CN';

  // 获取用户角色显示文本
  const getUserRoleText = (role: string) => {
    const roleMap: Record<string, string> = {
      admin: t('common:role.admin'),
      exchange: t('common:role.exchange'),
      issuer: t('common:role.issuer'),
      service_provider: t('common:role.serviceProvider'),
      intermediary: t('common:role.intermediary'),
      operator: t('common:role.operator'),
    };
    return roleMap[role] || t('common:role.user');
  };

  // 获取用户显示名称
  // 当非中文语言环境下，若用户名为中文则回退到角色名或邮箱
  const getUserDisplayName = () => {
    if (!user) return t('common:header.notLoggedIn');
    const name = user.name || '';
    if (isNonChineseLocale && hasChinese(name)) {
      return getUserRoleText(user.role);
    }
    return name || user.email || t('common:header.user');
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/auth/login');
  };

  // 获取角色文本（避免与用户名重复显示）
  const getRoleDisplay = () => {
    if (!user) return null;
    const roleText = getUserRoleText(user.role);
    const displayName = getUserDisplayName();
    // 如果用户名和角色名相同，不重复显示
    if (roleText === displayName) return null;
    return roleText;
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            {getUserDisplayName()}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>{user?.email}</div>
          {getRoleDisplay() && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {getRoleDisplay()}
            </div>
          )}
          {user?.companyName && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {isNonChineseLocale && hasChinese(user.companyName)
                ? t('common:app.title')
                : user.companyName}
            </div>
          )}
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    // TODO: 以下功能暂未实现，暂时隐藏
    // {
    //   key: 'profile',
    //   label: t('common:header.profile'),
    //   icon: <UserOutlined />,
    // },
    // {
    //   key: 'settings',
    //   label: t('common:header.settings'),
    //   icon: <SettingOutlined />,
    // },
    // {
    //   type: 'divider',
    // },
    {
      key: 'logout',
      label: t('common:header.logout'),
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  return (
    <Header
      style={{
        background:
          'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 32px',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        boxShadow:
          '0 4px 20px rgba(16, 185, 129, 0.3), 0 2px 8px rgba(16, 185, 129, 0.2)',
        height: 64,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setCollapsed(!collapsed)}
          style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '10px',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
          }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 20px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(12px)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <DashboardOutlined
              style={{
                fontSize: '22px',
                color: '#fff',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              }}
            />
            <Text
              strong
              style={{
                fontSize: '18px',
                color: '#fff',
                background: 'transparent',
                fontWeight: '700',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              }}
            >
              {t('common:app.title')}
            </Text>
          </div>

          {company && (
            <div
              style={{
                padding: '8px 16px',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(8px)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Text
                style={{
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontSize: '14px',
                  fontWeight: 600,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                }}
              >
                {company.name || t('common:app.companyService')}
              </Text>
            </div>
          )}
        </div>
      </div>

      <Space size="large">
        <LanguageSwitcher
          showLabel={false}
          size="middle"
          buttonStyle={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '10px',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
          }}
        />

        <Button
          type="text"
          icon={<BellOutlined />}
          style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '10px',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
          }}
        />

        <Button
          type="text"
          icon={<QuestionCircleOutlined />}
          style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '10px',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
          }}
        />

        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          trigger={['click']}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '8px 16px',
              borderRadius: '14px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Avatar
              icon={<UserOutlined />}
              src={user?.avatar}
              style={{
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(8px)',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                color: 'rgba(255, 255, 255, 0.9)',
              }}
              size="small"
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Text
                style={{
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontSize: '14px',
                  fontWeight: 600,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  lineHeight: '18px',
                }}
              >
                {getUserDisplayName()}
              </Text>
              <Text
                style={{
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: '12px',
                  fontWeight: 400,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  lineHeight: '16px',
                }}
              >
                {user
                  ? getUserRoleText(user.role)
                  : t('common:header.notLoggedIn')}
              </Text>
            </div>
          </div>
        </Dropdown>
      </Space>
    </Header>
  );
};

export default GlobalHeader;
